import React, {useEffect, useRef, useState} from 'react';
import {styled, Box} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import {media} from '@/styles/mediaQueries';

interface StickyHeaderProps {
  children: React.ReactNode;
  className?: string;
  paddingTop?: string;
  paddingBottom?: string;
}

const shouldNotForwardProps = ['isStuck', 'paddingTop', 'paddingBottom'];

const StyledStickyHeader = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{isStuck: boolean; paddingTop: string; paddingBottom: string}>(
  ({isStuck, paddingTop, paddingBottom}) => ({
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: isStuck ? 10 : undefined,
    backgroundColor: designSystemToken('semantic.bg.secondary'),
    transition: 'box-shadow 0.2s ease-in-out',
    marginLeft: isStuck ? '-50px' : '0px',
    marginRight: isStuck ? '-50px' : '0px',
    paddingLeft: isStuck ? '50px' : '0px',
    paddingRight: isStuck ? '50px' : '0px',
    paddingTop,
    paddingBottom,
    [media.mobile]: {
      marginLeft: isStuck ? '-20px' : '0px',
      marginRight: isStuck ? '-20px' : '0px',
      paddingLeft: isStuck ? '20px' : '0px',
      paddingRight: isStuck ? '20px' : '0px',
    },
  })
);

const SeparatorLine = styled('div')<{isVisible: boolean}>(({isVisible}) => ({
  position: 'absolute',
  height: '1px',
  width: '100%',
  left: '0',
  boxShadow: '0 2px 2px rgba(0, 0, 0, .06)',
  opacity: isVisible ? 1 : 0,
  transition: 'opacity .1s ease',
  bottom: 0,
}));

export default function StickyHeader({
  children,
  className,
  paddingTop = '20px',
  paddingBottom = '20px',
}: StickyHeaderProps) {
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the sentinel is not visible, the sticky header is stuck
        setIsStuck(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -1px 0px', // Trigger just before the sentinel disappears
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {/* Invisible sentinel element that helps detect when sticky behavior activates */}
      <div
        ref={sentinelRef}
        style={{
          width: '1px',
          height: '1px',
          pointerEvents: 'none',
        }}
      />
      <StyledStickyHeader
        className={className}
        isStuck={isStuck}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
      >
        {children}
        <SeparatorLine isVisible={isStuck} />
      </StyledStickyHeader>
    </>
  );
}
